import React from 'react';

import { type UseQueryOptions, useQuery } from '@lumapps/base-api/react-query';
import type { DocumentV1 } from '@lumapps/medias-document/types';

import { type GetDocumentParams, documentsQueryKeys, getDocument } from '../api';

export interface UseGetDocumentParams extends GetDocumentParams {
    /**
     * Optional callback to execute if the API call fails.
     */
    onError?: () => void;
}

/**
 * A hook to fetch the data for a single document using its document path.
 *
 * @param {UseGetDocumentParams} params
 * @param {UseQueryOptions<DocumentV1>} options
 */
export const useGetDocument = ({ docPath, onError }: UseGetDocumentParams, options?: UseQueryOptions<DocumentV1>) => {
    const query = useQuery<DocumentV1>({
        ...options,
        queryKey: documentsQueryKeys.getDocument({ docPath }),
        queryFn: async ({ signal }) => getDocument({ docPath }, signal),
    });

    /**
     * If the API call results in an error, we execute the provided
     * onError callback.
     */
    React.useEffect(() => {
        if (query.isError && onError) {
            onError();
        }
    }, [onError, query.isError]);

    return query;
};
